/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { useMemo, useState } from 'react'

import { Autocomplete, Icon, Tooltip } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'

import MDBadgeDot from 'components/MDBadgeDot'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { ROLE_LEVEL } from 'constants/roles'
import { useAuth } from 'context/auth'
import ComplexStatisticsCard from 'modules/Cards/StatisticsCards/ComplexStatisticsCard'
import HorizontalBarChart from 'modules/Charts/BarCharts/HorizontalBarChart'
import DefaultLineChart from 'modules/Charts/LineCharts/DefaultLineChart'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import { ROUTE_PATHS } from 'routes'
import { AnalyticsData } from 'types/server/dashboard'

import { ALL_CARD_FEEDBACK_TYPE_OPTIONS, ALL_TIMEFRAME_OPTIONS } from './constants'
import { CardsFeedbackType, TimeFrames } from './types'

function Analytics(): JSX.Element {
	const { user } = useAuth()
	const navigate = useNavigate()
	const [activeTimeFrame, setActiveTimeFrame] = useState<TimeFrames>('Last 7 Days')
	const [cardFeedbackTypes, setCardFeedbackTypes] = useState<CardsFeedbackType>('All')

	const fetchData = async () => {
		const response = await axios.get<AnalyticsData>(`${process.env.REACT_APP_API_URL}/analytics`, {
			params: { timeframe: activeTimeFrame, feedbackType: cardFeedbackTypes },
			headers: {
				Authorization: await user.auth.getIdToken(),
			},
		})
		return response.data
	}

	const { data, error, isLoading } = useQuery({
		queryKey: [`analytics-${cardFeedbackTypes}-${activeTimeFrame}`],
		queryFn: fetchData,
	})

	const handleReviewClick = () => {
		navigate(`${ROUTE_PATHS.FEEDBACK.TABLE}?stage=Awaiting approval`)
	}

	const lineChartData = useMemo(() => {
		if (!data?.lineChart) return null

		return {
			labels: data.lineChart.labels,
			datasets: data.lineChart.datasets,
		}
	}, [data?.lineChart])

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Grid container spacing={3}>
				<Grid item xs={12} md={6} lg={4}>
					{ROLE_LEVEL[2].includes(user.data.role) && data?.cards?.awaitingApproval?.value > 0 && (
						<MDBox display="flex" alignItems="center" justifyContent="center" height="100%">
							<MDTypography variant="body2" color="secondary" component="span">
								You have{' '}
								<MDTypography variant="body2" color="error" component="span">
									<b>
										<i>{data.cards.awaitingApproval.value}</i>
									</b>{' '}
								</MDTypography>
								&nbsp;feedback items awaiting approval.&nbsp;
								<MDTypography
									component="span"
									variant="button"
									fontWeight="medium"
									color="secondary"
									style={{ display: 'inline', cursor: 'pointer' }}
									onClick={handleReviewClick}
								>
									Review now.
								</MDTypography>
							</MDTypography>
						</MDBox>
					)}
				</Grid>
				<Grid item xs={12} md={6} lg={4}>
					<MDBox display="flex" alignItems="center" justifyContent={'flex-end'}>
						<MDTypography variant="h6" color="secondary">
							Cards Feedback Type:&nbsp;&nbsp;&nbsp;
						</MDTypography>
						<Autocomplete
							disableClearable
							value={cardFeedbackTypes}
							options={ALL_CARD_FEEDBACK_TYPE_OPTIONS}
							onChange={(_, newValue) => {
								setCardFeedbackTypes(newValue)
							}}
							size="medium"
							sx={{ width: '12rem' }}
							renderInput={params => <MDInput {...params} />}
						/>
					</MDBox>
				</Grid>
				<Grid item xs={12} md={6} lg={4}>
					<MDBox display="flex" alignItems="center" justifyContent={'flex-end'}>
						<MDTypography variant="h6" color="secondary">
							Timeframe:&nbsp;&nbsp;&nbsp;
						</MDTypography>
						<Autocomplete
							disableClearable
							value={activeTimeFrame}
							options={ALL_TIMEFRAME_OPTIONS}
							onChange={(_, newValue) => {
								setActiveTimeFrame(newValue)
							}}
							size="medium"
							sx={{ width: '10rem' }}
							renderInput={params => <MDInput {...params} />}
						/>
					</MDBox>
				</Grid>
				<Grid item xs={12} md={6} lg={4}>
					<MDBox mb={1.5}>
						{!isLoading && !error ? (
							<ComplexStatisticsCard
								color="secondary"
								icon="format_list_numbered"
								title="Created"
								count={`${data.cards?.created.value}`}
								data={data.cards?.created.data}
							/>
						) : (
							<LoadingComponent type="box" />
						)}
					</MDBox>
				</Grid>
				<Grid item xs={12} md={6} lg={4}>
					<MDBox mb={1.5}>
						{!isLoading && !error ? (
							<ComplexStatisticsCard
								color="secondary"
								icon="format_list_numbered"
								title="Investigating"
								count={`${data.cards?.investigating.value}`}
								data={data.cards?.investigating.data}
							/>
						) : (
							<LoadingComponent type="box" />
						)}
					</MDBox>
				</Grid>
				<Grid item xs={12} md={6} lg={4}>
					<MDBox mb={1.5}>
						{!isLoading && !error ? (
							<ComplexStatisticsCard
								color="secondary"
								icon="format_list_numbered"
								title="Member Outcome"
								count={`${data.cards?.memberOutcome.value}`}
								data={data.cards?.memberOutcome.data}
							/>
						) : (
							<LoadingComponent type="box" />
						)}
					</MDBox>
				</Grid>
				<Grid item xs={12} md={6} lg={4}>
					<MDBox mb={1.5}>
						{!isLoading && !error ? (
							<ComplexStatisticsCard
								color="secondary"
								icon="format_list_numbered"
								title="Customer Outcome"
								count={`${data.cards?.customerOutcome.value}`}
								data={data.cards?.customerOutcome.data}
							/>
						) : (
							<LoadingComponent type="box" />
						)}
					</MDBox>
				</Grid>
				<Grid item xs={12} md={6} lg={4}>
					<MDBox mb={1.5}>
						{!isLoading && !error ? (
							<ComplexStatisticsCard
								color="success"
								icon="inbox"
								title="Completed"
								count={data.cards?.complete.value}
								data={data.cards?.complete.data}
							/>
						) : (
							<LoadingComponent type="box" />
						)}
					</MDBox>
				</Grid>
				<Grid item xs={12} md={6} lg={4}>
					<MDBox mb={1.5}>
						{!isLoading && !error ? (
							<ComplexStatisticsCard
								icon="timer"
								title="Overdue"
								color="error"
								count={data.cards?.overdue.value}
								data={data.cards?.overdue.data}
							/>
						) : (
							<LoadingComponent type="box" />
						)}
					</MDBox>
				</Grid>
			</Grid>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					{!isLoading && !error ? (
						<DefaultLineChart
							title="Feedback Volume (Over the Last 12 Months)"
							description={
								<MDBox display="flex" justifyContent="space-between">
									<MDBox display="flex" ml={-1}>
										<MDBadgeDot color="success" size="sm" badgeContent="Commendations" />
										<MDBadgeDot color="error" size="sm" badgeContent="Complaint" />
									</MDBox>
									<MDBox mt={-4} mr={-1} position="absolute" right="1.5rem">
										<Tooltip
											title="See how the feedback volume changes over time (L12M)"
											placement="left"
											arrow
										>
											<MDButton variant="outlined" color="secondary" size="small" circular iconOnly>
												<Icon>priority_high</Icon>
											</MDButton>
										</Tooltip>
									</MDBox>
								</MDBox>
							}
							chart={lineChartData}
						/>
					) : (
						<LoadingComponent type="box" />
					)}
				</Grid>
				<Grid item xs={12} md={6}>
					{!isLoading && !error ? (
						<HorizontalBarChart
							title={data?.complaintLeaderboard?.title}
							chart={data?.complaintLeaderboard?.chart}
						/>
					) : (
						<LoadingComponent type="box" />
					)}
				</Grid>
				<Grid item xs={12} md={6}>
					{!isLoading && !error ? (
						<HorizontalBarChart
							title={data?.commendationLeaderboard?.title}
							chart={data?.commendationLeaderboard?.chart}
						/>
					) : (
						<LoadingComponent type="box" />
					)}
				</Grid>
				<Grid item xs={12} md={6}>
					<MDBox mb={1.5}>
						{!isLoading && !error ? (
							<ComplexStatisticsCard
								color="info"
								icon="currency_pound"
								title="Total Spend (£)"
								count={`${data.cards?.totalSpend.value}`}
								data={data.cards?.totalSpend.data}
							/>
						) : (
							<LoadingComponent type="box" />
						)}
					</MDBox>
				</Grid>
				<Grid item xs={12} md={6}>
					<MDBox mb={1.5}>
						{!isLoading && !error ? (
							<ComplexStatisticsCard
								color="info"
								icon="av_timer"
								title="Average Turnaround Time (Days)"
								count={`${data.cards?.avgTurnaround.value}`}
								data={data.cards?.avgTurnaround.data}
							/>
						) : (
							<LoadingComponent type="box" />
						)}
					</MDBox>
				</Grid>
			</Grid>
		</DashboardLayout>
	)
}

export default Analytics
